import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  uploadStaging,
  buildTrainingQueue,
  buildTrainingQueueByLayout,
  listTrainingCards,
  listTrainingBoards,
  patchTrainingBoard,
  submitTrainingCard,
  skipTrainingCard,
  deleteTrainingCard,
  getTrainingStats,
  applyCertRules,
  listBoardLayouts,
  fileUrl,
  recropPendingTrainingCards,
  fetchTrainingCardSourcePhotoBlobUrl,
  deleteTrainingBoard,
  deleteTrainingQueue,
} from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Camera, Loader2, Sparkles, SkipForward, CheckCircle2, X, Trash2,
  Brain, Flame, Target, ChevronLeft, Zap, LayoutGrid, Aperture, Move,
} from 'lucide-react';
import PromoteToLibraryButton from '../../components/PromoteToLibraryButton';
import CertTrainFrameAdjuster from '../../components/CertTrainFrameAdjuster';
import { toast } from 'sonner';
import BoardGrid from '../../components/BoardGrid';

const EMPTY = { description: '', mcbType: 'B', mcbRating: '', rcdRating: '', bsEn: '', breakingCapacityKa: '', isThreePole: false };

const FIELD_LABELS = {
  description: 'Circuit description',
  mcbType: 'MCB type',
  mcbRating: 'MCB rating',
  rcdRating: 'RCD rating',
  bsEn: 'BS EN standard',
  breakingCapacityKa: 'Breaking capacity (kA)',
  isThreePole: '3-pole breaker',
};

// Common BS EN standards engineers see on UK distribution boards.
// Powered by an HTML5 <datalist> so engineers can type freely too —
// this is just the autocomplete shortlist.
const BS_EN_OPTIONS = [
  { value: '60898', label: '60898 — MCB' },
  { value: '61008', label: '61008 — RCD / RCCB' },
  { value: '61009', label: '61009 — RCBO' },
  { value: '60947-2', label: '60947-2 — MCCB / industrial' },
  { value: '60269', label: '60269 — HRC fuse' },
  { value: '62606', label: '62606 — AFDD' },
];

const SCHEME_LABELS = {
  three_phase_by_row: '3-phase, numbered by row (1L1, 1L2, 1L3, 2L1…)',
  single_phase_ltr: 'Single-phase, left → right (1, 2, 3…)',
  single_phase_rtl: 'Single-phase, right → left (1, 2, 3…)',
  unknown: 'Unknown — please pick one',
};

const SCHEME_SHORT = {
  three_phase_by_row: '3-phase · row order',
  single_phase_ltr: 'Single-phase · L→R',
  single_phase_rtl: 'Single-phase · R→L',
  unknown: 'Layout unknown',
};

const StatTile = ({ icon: Icon, label, value, tone = 'slate', testId }) => (
  <div
    className={`flex items-center gap-3 px-4 py-3 rounded-md border bg-white border-${tone}-100`}
    data-testid={testId}
  >
    <div className={`h-9 w-9 rounded-md flex items-center justify-center bg-${tone}-50 text-${tone}-700`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  </div>
);

const CertTrain = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const fileInputRef = useRef(null);
  const [cards, setCards] = useState([]);
  const [boards, setBoards] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [circuitNumber, setCircuitNumber] = useState('');
  const [isSpare, setIsSpare] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [streak, setStreak] = useState(0);
  const [sessionLabeled, setSessionLabeled] = useState(0);
  const [flashDelta, setFlashDelta] = useState(null);
  const [derived, setDerived] = useState({});  // fields auto-derived from rules
  const [layouts, setLayouts] = useState([]);
  // When the engineer picks a layout, every uploaded photo is sliced
  // into one mini-card per frame (one breaker per crop), so the
  // training game becomes per-frame instead of per-whole-board.
  const [selectedLayoutId, setSelectedLayoutId] = useState('');
  // True while a "Re-crop pending cards" admin run is in flight on the
  // active board's queue. Engineers reach for this button when a
  // cropper bug is fixed mid-queue — the persisted `cropBytes` on
  // already-pending cards still carry the old buggy crops, so we
  // re-cut them in place using the current cropping math.
  const [recropping, setRecropping] = useState(false);
  // Per-card "Adjust crop & re-run AI" dialog state. The engineer
  // drags the bounding box on the original layout photo, hits Re-run,
  // and we swap the active card in place with the fresh AI guess.
  const [adjusterOpen, setAdjusterOpen] = useState(false);
  // Pre-fetched source-photo blob URL for the currently active card.
  // Two-card lookahead — the active card's photo AND the next pending
  // card's photo are kept in `photoCacheRef` (a Map keyed by cardId)
  // so the moment the engineer arrow-rights onto the next card the
  // adjuster opens instantly. Cache is bounded to the {current, next}
  // pair so we don't pile up megabytes of decoded JPEGs on long
  // queues — entries that aren't either of those are revoked + dropped.
  const [prefetchedPhotoUrl, setPrefetchedPhotoUrl] = useState(null);
  const prefetchedPhotoCardIdRef = useRef(null);
  const photoCacheRef = useRef(new Map());

  const loadAll = async () => {
    const [q, s, b, lo] = await Promise.all([
      listTrainingCards(),
      getTrainingStats(),
      listTrainingBoards(),
      listBoardLayouts().catch(() => ({ layouts: [] })),
    ]);
    setCards(q.cards || []);
    setStats(s);
    setBoards(b.boards || []);
    setLayouts(lo.layouts || []);
    return q.cards || [];
  };

  useEffect(() => { loadAll(); }, []);

  // When the engineer arrives via the "Send to AI Training" button on
  // the cert page, the navigation state carries the staged photo IDs
  // and the chosen layout. Auto-trigger the layout-aware queue
  // endpoint so the deck of frame-crops appears immediately — no
  // re-upload, no second layout pick.
  useEffect(() => {
    const seedPhotoIds = location.state?.seedPhotoIds;
    const seedLayoutId = location.state?.seedLayoutId;
    if (!Array.isArray(seedPhotoIds) || !seedPhotoIds.length || !seedLayoutId) return;
    // Wipe nav state so a hard refresh / back-nav doesn't re-fire.
    window.history.replaceState({}, '');
    setSelectedLayoutId(seedLayoutId);
    (async () => {
      setExtracting(true);
      try {
        const res = await buildTrainingQueueByLayout(seedPhotoIds, seedLayoutId, '');
        const layoutName = location.state?.seedLayoutName || 'layout';
        toast.success(
          `${res.cardsAdded} crop${res.cardsAdded === 1 ? '' : 's'} added from ${res.photosUsed} photo${res.photosUsed === 1 ? '' : 's'} (${layoutName})`
        );
        await loadAll();
        setActiveIdx(0);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not build training deck from cert page');
      } finally {
        setExtracting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.seedPhotoIds, location.state?.seedLayoutId]);

  // Whenever the card list / active index changes, hydrate the form
  // with the AI's guess so the engineer can confirm or correct.
  useEffect(() => {
    const pending = cards.filter(c => c.status === 'pending');
    const card = pending[activeIdx];
    if (card) {
      setForm({
        description: card.aiGuess?.description || '',
        mcbType: card.aiGuess?.mcbType || 'B',
        mcbRating: card.aiGuess?.mcbRating || '',
        rcdRating: card.aiGuess?.rcdRating || '',
        bsEn: card.aiGuess?.bsEn || '',
        breakingCapacityKa: card.aiGuess?.breakingCapacityKa || '',
        isThreePole: !!card.aiGuess?.isThreePole,
      });
      setCircuitNumber(card.circuitNumber || card.aiCircuitNumber || '');
      setIsSpare(Boolean(card.isSpare));
    } else {
      setForm(EMPTY);
      setCircuitNumber('');
      setIsSpare(false);
    }
  }, [cards, activeIdx]);

  const pendingCards = useMemo(() => cards.filter(c => c.status === 'pending'), [cards]);
  const card = pendingCards[activeIdx] || null;
  const aiGuess = card?.aiGuess || {};
  const activeBoard = card ? boards.find(b => b.id === card.boardId) : null;
  const nextCard = pendingCards[activeIdx + 1] || null;

  // Keyboard shortcuts: arrow keys step between pending cards, `A`
  // opens the manual crop adjuster on the active card. We bail when
  // the user is typing inside an input / textarea / contenteditable
  // so the shortcuts don't fire mid-word as the engineer fills in
  // descriptions or ratings. Disabled while the adjuster dialog is
  // already open — the dialog has its own ESC / R handlers.
  useEffect(() => {
    const handler = (e) => {
      if (adjusterOpen) return;
      const t = e.target;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!pendingCards.length) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(pendingCards.length - 1, i + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if ((e.key === 'a' || e.key === 'A') && card?.layoutId && card?.frameId
                 && card?.frameType !== 'circuit_label') {
        e.preventDefault();
        setAdjusterOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [adjusterOpen, pendingCards.length, card?.layoutId, card?.frameId, card?.frameType]);

  // Background pre-fetch the source photo for the currently active
  // card AND the next pending card. The blob URLs stay cached in
  // `photoCacheRef` (Map<cardId, blobUrl>) bounded to those two
  // entries — anything else is revoked + dropped each pass so we
  // don't leak megabytes of decoded JPEGs on long queues. Skips
  // cards that wouldn't trigger the adjuster anyway (no layout
  // frame, label-only frame).
  useEffect(() => {
    if (!card?.id || !card?.layoutId || !card?.frameId
        || card.frameType === 'circuit_label') {
      return undefined;
    }
    const cache = photoCacheRef.current;
    let cancelled = false;

    const isAdjustable = (c) => (
      c && c.id && c.layoutId && c.frameId && c.frameType !== 'circuit_label'
    );
    const ensureFetched = async (targetCard) => {
      if (!isAdjustable(targetCard)) return;
      if (cache.has(targetCard.id)) return;
      try {
        const url = await fetchTrainingCardSourcePhotoBlobUrl(targetCard.id);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        cache.set(targetCard.id, url);
      } catch (_) {
        // Silent — adjuster falls back to its own fetch + a friendly
        // error message if the engineer happens to open it.
      }
    };

    (async () => {
      // 1. Ensure the active card's photo is cached, then expose it
      //    to the adjuster.
      await ensureFetched(card);
      if (cancelled) return;
      const currentUrl = cache.get(card.id) || null;
      prefetchedPhotoCardIdRef.current = currentUrl ? card.id : null;
      setPrefetchedPhotoUrl(currentUrl);

      // 2. Prefetch the next pending card. By the time the engineer
      //    arrow-rights onto it, its photo is already cached.
      if (isAdjustable(nextCard)) await ensureFetched(nextCard);
      if (cancelled) return;

      // 3. Evict any cache entries outside the {current, next} pair.
      const keep = new Set([card.id, nextCard?.id].filter(Boolean));
      for (const [cid, url] of Array.from(cache.entries())) {
        if (!keep.has(cid)) {
          URL.revokeObjectURL(url);
          cache.delete(cid);
        }
      }
    })();
    return () => { cancelled = true; };
    // We DELIBERATELY depend on the card / nextCard *id* fields rather
    // than the object identity — `pendingCards.filter()` produces a
    // new array on every render, and depending on the array would
    // re-fire this effect every render and saturate production's
    // network with concurrent prefetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, card?.layoutId, card?.frameId, card?.frameType, nextCard?.id, nextCard?.layoutId, nextCard?.frameId, nextCard?.frameType]);

  // On unmount: drain the cache so we don't leak blob URLs across
  // route changes.
  useEffect(() => () => {
    const cache = photoCacheRef.current;
    for (const url of cache.values()) URL.revokeObjectURL(url);
    cache.clear();
  }, []);

  // Re-evaluate cert rules whenever the form changes. Lightly debounced
  // via a setTimeout so we don't hammer the API on every keystroke.
  useEffect(() => {
    if (!card) { setDerived({}); return; }
    const ctx = {
      ...form,
      circuitNumber,
      isSpare: String(isSpare),
      phases: String(activeBoard?.phases || ''),
      numberingScheme: activeBoard?.numberingScheme || '',
      wayCount: String(activeBoard?.wayCount || ''),
    };
    const t = setTimeout(async () => {
      try {
        const { derived } = await applyCertRules(ctx, 'card');
        setDerived(derived || {});
      } catch (e) {
        // Non-fatal — rule errors shouldn't break card editing
        setDerived({});
      }
    }, 300);
    return () => clearTimeout(t);
  }, [form, circuitNumber, isSpare, card?.id, activeBoard?.phases, activeBoard?.numberingScheme, activeBoard?.wayCount]);

  // Click a cell on the topology grid → jump that pending card into
  // the active editor. Done/spare cells re-open if the engineer wants
  // to revise (they swap state from pending → done so re-clicking
  // brings up the now-empty editor with the same photo, and the
  // existing record stays as-is unless the engineer overwrites it).
  const jumpToCard = (cardId) => {
    const target = cards.find(c => c.id === cardId);
    if (!target) return;
    if (target.status === 'pending') {
      const idx = pendingCards.findIndex(c => c.id === cardId);
      if (idx >= 0) {
        setActiveIdx(idx);
        // Scroll to active card
        const node = document.querySelector('[data-testid="active-card"]');
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      toast.message(target.isSpare || target.final?.isSpare
        ? 'This way is already marked as spare'
        : 'This way is already labelled');
    }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSelectFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    const stagedIds = [];
    try {
      for (const file of list) {
        // iPhone Camera-app exports (HEIC) are re-encoded to JPEG by the
        // backend on upload — accept them here so the engineer doesn't
        // have to convert them on a desktop first.
        const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
        const heicSuffix = /\.(heic|heif|heics|heifs)$/i.test(file.name || '');
        if (!allowedMimes.includes(file.type) && !heicSuffix) {
          toast.error(`${file.name}: only JPG / PNG / WEBP / HEIC supported`);
          continue;
        }
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 15 MB)`);
          continue;
        }
        const meta = await uploadStaging(file);
        stagedIds.push(meta.storedAs);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (!stagedIds.length) return;
    setExtracting(true);
    try {
      const res = selectedLayoutId
        ? await buildTrainingQueueByLayout(stagedIds, selectedLayoutId, '')
        : await buildTrainingQueue(stagedIds, '');
      const lines = [`Added ${res.cardsAdded} card${res.cardsAdded === 1 ? '' : 's'}`];
      if (res.framesPerPhoto) lines.push(`${res.framesPerPhoto} frames × ${res.photosUsed} photo${res.photosUsed === 1 ? '' : 's'}`);
      if (res.spareWaysDetected) lines.push(`${res.spareWaysDetected} spare${res.spareWaysDetected === 1 ? '' : 's'} detected`);
      toast.success(lines.join(' · '));
      await loadAll();
      setActiveIdx(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const handleRecropPending = async () => {
    // Recrop runs against ALL pending cards in the active queue —
    // useful right after a cropper-math fix has been redeployed and
    // the engineer doesn't want to abandon hundreds of half-labelled
    // cards just to get fresh crops. We pull the queueId from the
    // active board's `queueId` (every card on a queue shares it).
    const queueId = card?.queueId || activeBoard?.queueId;
    if (!queueId) {
      toast.error('No active queue to re-crop');
      return;
    }
    if (!window.confirm(`Re-crop all pending cards on this queue using the latest cropping math? This rewrites the crop image on each card; nothing else changes.`)) {
      return;
    }
    setRecropping(true);
    try {
      const res = await recropPendingTrainingCards(queueId);
      toast.success(
        `Re-cropped ${res.recropped} card${res.recropped === 1 ? '' : 's'}` +
        (res.skippedNoPhoto ? ` · ${res.skippedNoPhoto} skipped (no photo)` : '') +
        (res.skippedNoFrame ? ` · ${res.skippedNoFrame} skipped (no frame)` : '') +
        (res.skippedCropFailed ? ` · ${res.skippedCropFailed} crop failed` : ''),
      );
      await loadAll();
    } catch (e) {
      console.error('Recrop failed', e);
      toast.error(e?.response?.data?.detail || 'Re-crop failed');
    } finally {
      setRecropping(false);
    }
  };

  const handleSchemeChange = async (newScheme) => {
    if (!activeBoard || newScheme === activeBoard.numberingScheme) return;    try {
      const newPhases = newScheme === 'three_phase_by_row' ? 3 : 1;
      await patchTrainingBoard(activeBoard.id, {
        numberingScheme: newScheme,
        phases: newPhases,
      });
      toast.success('Renumbered all pending cards on this board');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update layout');
    }
  };

  const handleWayCountChange = async (newCount) => {
    if (!activeBoard || newCount === activeBoard.wayCount) return;
    try {
      await patchTrainingBoard(activeBoard.id, { wayCount: newCount });
      toast.success(`Set way count to ${newCount}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update way count');
    }
  };

  const handleSubmit = async () => {
    if (!card) return;
    const ft = card.frameType || 'breaker';
    if (ft === 'breaker') {
      if (!isSpare && !form.description.trim() && !form.mcbRating.trim()) {
        return toast.error('Fill at least the description or MCB rating before saving (or mark as spare).');
      }
    } else {
      // circuit_label / isolator: only description matters.
      if (!form.description.trim()) {
        return toast.error('Fill the description before saving.');
      }
    }
    setSubmitting(true);
    try {
      // Strip fields that don't belong to this frame type so the
      // `cert_corrections` stream isn't polluted with empty MCB /
      // RCD / BS-EN values for label-only cards.
      const payload = ft === 'breaker'
        ? { ...form, circuitNumber, isSpare }
        : ft === 'isolator'
          ? { description: form.description, bsEn: form.bsEn, circuitNumber, isSpare: false }
          : { description: form.description, circuitNumber, isSpare: false };
      const res = await submitTrainingCard(card.id, payload);
      const editedCount = Object.values(res.delta || {}).filter(v => v === 'corrected').length;
      const keptCount = Object.values(res.delta || {}).filter(v => v === 'kept').length;
      const gangSiblings = Array.isArray(res.gangSiblingIds) ? res.gangSiblingIds : [];
      // Each labelled card represents one physical way, so a 3-pole
      // gang collapse advances the session counter by 1 (the L1 card)
      // + every auto-completed sibling (L2 + L3). The backend stats
      // mirror this — gang siblings flip to done so `done`, `pending`,
      // and the BoardGrid % bar all reflect 3 ways covered for one
      // engineer click.
      const waysAdded = 1 + gangSiblings.length;
      setStreak(s => s + 1);
      setSessionLabeled(s => s + waysAdded);
      setFlashDelta({ ...res.delta, _isSpare: res.isSpare });
      setCards(prev => prev.map(c => {
        if (c.id === card.id) return { ...c, status: 'done' };
        if (gangSiblings.includes(c.id)) return { ...c, status: 'done' };
        return c;
      }));
      setTimeout(() => setFlashDelta(null), 900);
      setActiveIdx(0);
      const stats = await getTrainingStats();
      setStats(stats);
      if (res.isSpare) {
        toast.success(`Spare way logged · ${circuitNumber || ''}`);
      } else if (gangSiblings.length > 0) {
        toast.success(`Logged 3-pole gang · ${res.circuitNumber || ''} (${gangSiblings.length + 1} cards collapsed)`);
      } else if (editedCount === 0 && keptCount > 0) {
        toast.success(`Nice — AI got all ${keptCount} field${keptCount === 1 ? '' : 's'} right!`);
      } else if (editedCount > 0) {
        toast.success(`Logged · ${editedCount} correction${editedCount === 1 ? '' : 's'} fed back to AI`);
      } else {
        toast.success('Logged');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!card) return;
    try {
      await skipTrainingCard(card.id);
      setCards(prev => prev.map(c => c.id === card.id ? { ...c, status: 'skipped' } : c));
      setStreak(0);
      setActiveIdx(0);
      toast.message('Skipped');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Skip failed');
    }
  };

  const handleDelete = async () => {
    if (!card) return;
    if (!window.confirm('Remove this card from the queue? It won\'t be labelled.')) return;
    try {
      await deleteTrainingCard(card.id);
      setCards(prev => prev.filter(c => c.id !== card.id));
      setStreak(0);
      setActiveIdx(0);
      toast.message('Card removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const fieldHint = (field) => {
    const a = aiGuess[field] || '';
    const f = form[field] || '';
    if (!a) return null;
    if (a === f) return <span className="text-xs text-emerald-600">AI guessed: <span className="font-medium">{a}</span> · keep to confirm</span>;
    return <span className="text-xs text-amber-600">AI guessed: <span className="font-medium line-through">{a}</span> · you&apos;re correcting it</span>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 overflow-x-hidden">
      <PageHeader
        title="AI Training Game"
        description="Upload board photos and label one circuit at a time. Every confirmation and every correction makes the next AI extraction smarter."
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(`${base}/certificates`)}
            data-testid="back-to-certs-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Certificates
          </Button>
        }
      />

      {/* Score / streak strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="training-stats">
        <StatTile icon={Flame} label="Streak" value={streak} tone="amber" testId="stat-streak" />
        <StatTile icon={Target} label="Pending" value={stats?.pending ?? 0} tone="blue" testId="stat-pending" />
        <StatTile icon={CheckCircle2} label="Today" value={stats?.todayLabeled ?? 0} tone="emerald" testId="stat-today" />
        <StatTile icon={Brain} label="Lifetime" value={stats?.lifetimeLabeled ?? 0} tone="slate" testId="stat-lifetime" />
      </div>

      {/* Upload zone */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display uppercase tracking-wide text-slate-900">1. Add board photos to the queue</h2>
            <p className="text-sm text-slate-600 mt-0.5">
              {selectedLayoutId
                ? 'Photo will be sliced into one mini-photo per frame in the chosen layout — play the deck and correct the AI on each frame.'
                : 'AI will scan each photo and break it into one card per circuit it detects.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Layout picker. When set, uploads route through the
                layout-aware queue endpoint so each frame becomes its
                own card with its own crop. Empty value means "no
                layout — use legacy whole-photo flow". */}
            <Select
              value={selectedLayoutId || '__none__'}
              onValueChange={(v) => setSelectedLayoutId(v === '__none__' ? '' : v)}
            >
              <SelectTrigger className="h-9 w-[260px]" data-testid="training-layout-select">
                <SelectValue placeholder="Attach a board layout (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No layout — auto-detect circuits</SelectItem>
                {layouts.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} · {(l.frames || []).length} frame{(l.frames || []).length === 1 ? '' : 's'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              className="hidden"
              onChange={e => onSelectFiles(e.target.files)}
              data-testid="training-upload-input"
            />
            <Button
              type="button"
              disabled={uploading || extracting}
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="training-upload-btn"
            >
              {(uploading || extracting) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading…' : extracting ? 'AI scanning…' : 'Add photos'}
            </Button>
          </div>
        </div>
      </section>

      {/* Active card */}
      <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="active-card">
        <div className="px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-700" />
            <h2 className="font-display uppercase tracking-wide text-slate-900 text-sm">2. Label one circuit</h2>
            {activeBoard && (
              <span
                className="text-xs text-slate-500 ml-2 px-2 py-0.5 rounded-full bg-slate-100 inline-flex items-center gap-1"
                data-testid="board-layout-chip"
              >
                <LayoutGrid className="h-3 w-3" /> {SCHEME_SHORT[activeBoard.numberingScheme] || 'Layout?'}
                · {activeBoard.wayCount || 0} ways
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecropPending}
              disabled={recropping || !pendingCards.length}
              title="Re-cut every pending card from its source layout photo using the latest cropping math. Use after redeploying a cropper fix."
              className="h-7 px-2 text-[11px] gap-1"
              data-testid="recrop-pending-btn"
            >
              {recropping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Aperture className="h-3 w-3" />}
              Re-crop pending
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                // Engineer-driven nuke of an entire queue. Useful when
                // a batch was uploaded with the wrong layout / settings
                // and the engineer would rather start over than fix it
                // card by card.
                const queueId = card?.queueId || activeBoard?.queueId || boards[0]?.queueId;
                if (!queueId) { toast.error('No active queue'); return; }
                const totalCards = cards.length;
                const totalBoards = boards.length;
                const ok = window.confirm(
                  `Delete the entire active queue?\n\nThis will remove ${totalBoards} board${totalBoards === 1 ? '' : 's'} and ${totalCards} training card${totalCards === 1 ? '' : 's'}. Source photos are kept.\n\nThis cannot be undone.`,
                );
                if (!ok) return;
                try {
                  const res = await deleteTrainingQueue(queueId);
                  toast.success(`Deleted queue · ${res.deletedBoards} boards · ${res.deletedCards} cards cascaded`);
                  setBoards([]); setCards([]); setActiveIdx(0);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Delete failed');
                }
              }}
              disabled={!boards.length}
              title="Delete this entire queue — every board topology and every training card on it. Source photos are kept."
              className="h-7 px-2 text-[11px] gap-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="delete-queue-btn"
            >
              <Trash2 className="h-3 w-3" />
              Delete queue
            </Button>
            <div className="text-xs text-slate-500 tabular-nums flex items-center gap-2">
              {pendingCards.length > 0 ? `${pendingCards.length} card${pendingCards.length === 1 ? '' : 's'} left` : 'Queue empty'}
              {pendingCards.length > 0 && (
                <span
                  className="hidden md:inline-flex items-center gap-1 text-[10px] text-slate-400 font-mono"
                  title="Keyboard: ← / → step between pending cards · A opens the crop adjuster · R re-runs the AI inside the adjuster"
                  data-testid="cert-train-shortcut-hint"
                >
                  <kbd className="px-1 rounded border border-slate-200 bg-slate-50 text-slate-600">←</kbd>
                  <kbd className="px-1 rounded border border-slate-200 bg-slate-50 text-slate-600">→</kbd>
                  <kbd className="px-1 rounded border border-slate-200 bg-slate-50 text-slate-600">A</kbd>
                </span>
              )}
            </div>
          </div>
        </div>

        {!card ? (
          <div className="p-12 text-center" data-testid="empty-queue">
            <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Brain className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="font-display uppercase tracking-wide text-slate-900 mb-1">Queue is empty</h3>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              Upload distribution-board photos above to add training cards. The AI will try its best — your edits teach it the patterns it&apos;s missing.
            </p>
          </div>
        ) : (
          <>
            {/* Board layout banner — engineer can override scheme/phases/wayCount */}
            {activeBoard && (
              <div
                className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-3 flex-wrap"
                data-testid="board-layout-banner"
              >
                <div className="text-xs text-slate-600 font-medium">Board layout</div>
                <Select
                  value={activeBoard.numberingScheme || 'unknown'}
                  onValueChange={handleSchemeChange}
                >
                  <SelectTrigger className="h-8 text-xs w-[260px]" data-testid="board-scheme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SCHEME_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-600">Ways</Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    defaultValue={activeBoard.wayCount || 0}
                    onBlur={e => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isNaN(n) && n !== activeBoard.wayCount) handleWayCountChange(n);
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    className="h-8 w-20 text-xs"
                    data-testid="board-waycount-input"
                  />
                </div>
                <span className="text-[11px] text-slate-500">
                  Changing the scheme renumbers every pending card on this photo.
                </span>
              </div>
            )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Photo — when this card was generated from a layout
                frame we show the cropped mini-photo as the focus and
                a small thumbnail of the full board for context. */}
            <div className="lg:col-span-3 min-w-0 bg-slate-900 flex flex-col items-center justify-center p-4 min-h-[320px] relative overflow-hidden">
              {card.frameLabel && (
                <div
                  className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-blue-600/90 text-white text-xs font-mono uppercase tracking-wide shadow"
                  data-testid="card-frame-label"
                >
                  {card.frameLabel}
                  {card.frameType && card.frameType !== 'breaker' && (
                    <span className="ml-2 opacity-80">· {card.frameType.replace('_', ' ')}</span>
                  )}
                </div>
              )}
              {/* Manual crop adjuster — engineer-driven re-extract for
                  cards where the AI auto-crop landed off the breaker
                  text. Only shown when the card has the layout/frame
                  context the backend needs to re-cut from. On mobile
                  we collapse to icon-only so the button doesn't bleed
                  off the right edge. */}
              {card.layoutId && card.frameId && card.frameType !== 'circuit_label' && (
                <button
                  type="button"
                  onClick={() => setAdjusterOpen(true)}
                  className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/95 hover:bg-white text-slate-900 text-xs font-medium shadow border border-slate-200 transition-colors max-w-[calc(100%-1.5rem)]"
                  title="Manually move/resize the crop region on the original layout photo, then re-run the AI on the new region. Shortcut: A"
                  data-testid="card-adjust-crop-btn"
                >
                  <Move className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="hidden sm:inline">Adjust crop &amp; re-run AI</span>
                  <span className="sm:hidden">Adjust</span>
                </button>
              )}
              <img
                src={fileUrl(card.cropUrl || card.photoUrl)}
                alt={card.frameLabel || 'Distribution board'}
                className="max-h-[480px] max-w-full w-auto object-contain rounded shadow-lg bg-white"
                data-testid="card-photo"
              />
              {card.cropUrl && card.photoUrl && (
                <a
                  href={fileUrl(card.photoUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-[11px] text-slate-300 hover:text-white"
                  data-testid="card-full-board-link"
                >
                  <img
                    src={fileUrl(card.photoUrl)}
                    alt="Full board"
                    className="h-12 w-auto rounded border border-slate-700 object-contain bg-slate-800"
                  />
                  Open full board photo ↗
                </a>
              )}
            </div>

            {/* Form */}
            <div className="lg:col-span-2 p-5 space-y-4 relative">
              {flashDelta && (
                <div
                  className="absolute inset-0 bg-emerald-50/90 backdrop-blur-sm flex items-center justify-center z-10 animate-in fade-in"
                  data-testid="flash-delta"
                >
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
                    <div className="font-display uppercase tracking-wide text-emerald-900">
                      {flashDelta._isSpare ? 'Spare way logged' : 'Logged'}
                    </div>
                    {!flashDelta._isSpare && (
                      <div className="text-xs text-emerald-700 mt-1">
                        {Object.entries(flashDelta).filter(([k]) => !k.startsWith('_')).map(([f, v]) => (
                          <span key={f} className="inline-block mr-2">
                            {v === 'kept' ? '✓' : v === 'corrected' ? '✏' : '＋'} {FIELD_LABELS[f]}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Circuit number badge (editable) + spare toggle */}
              <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Way</Label>
                  <Input
                    value={circuitNumber}
                    onChange={e => setCircuitNumber(e.target.value)}
                    className="h-9 w-20 font-mono text-base font-semibold text-slate-900 text-center"
                    data-testid="card-circuitnumber-input"
                  />
                  {form.isThreePole && /^\d+L1$/i.test(circuitNumber) && (
                    <span
                      className="text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                      data-testid="card-3pole-tp-hint"
                    >
                      → saves as {circuitNumber.replace(/L1$/i, 'TP')} (collapses L2 + L3)
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsSpare(s => !s)}
                  className={`ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                    isSpare
                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  } ${card.frameType && card.frameType !== 'breaker' ? 'hidden' : ''}`}
                  data-testid="card-spare-toggle"
                >
                  <Zap className="h-3.5 w-3.5" />
                  {isSpare ? 'Spare way' : 'Mark as spare'}
                </button>
              </div>

              {/* Field inputs (disabled when isSpare). The card's
                  `frameType` decides which fields are relevant:
                  • breaker (default) → full breaker spec — description,
                    MCB type / rating, RCD rating, BS EN.
                  • circuit_label → JUST the description text. The
                    label frame physically only contains the circuit
                    description card on the panel; the breaker spec
                    lives in the corresponding breaker frame.
                  • isolator → main-switch spec (description, optional
                    rating, BS EN) but no MCB curve. */}
              <div className={isSpare ? 'opacity-40 pointer-events-none' : ''}>
                <div>
                  <Label>
                    {card.frameType === 'circuit_label'
                      ? 'Circuit description (only field needed for label frames)'
                      : card.frameType === 'isolator'
                      ? 'Isolator description'
                      : 'Circuit description'}
                  </Label>
                  {/* Paired circuit_label evidence — only shown on
                      breaker cards that have a matching label frame
                      in the same layout. Lets the engineer cross-
                      check the breaker's reading against the printed
                      description card without flipping cards. */}
                  {card.pairedLabel && (
                    <div
                      className="mt-1.5 mb-2 flex items-stretch gap-2 rounded-md border border-blue-100 bg-blue-50/60 p-2"
                      data-testid="card-paired-label-chip"
                    >
                      {card.pairedLabel.cropUrl && (
                        <img
                          src={fileUrl(card.pairedLabel.cropUrl)}
                          alt={`Label for ${card.pairedLabel.frameLabel}`}
                          className="h-12 w-auto rounded border border-blue-200 object-contain bg-white flex-shrink-0"
                          data-testid="card-paired-label-crop"
                        />
                      )}
                      <div className="min-w-0 flex flex-col justify-center">
                        <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">
                          Paired label · {card.pairedLabel.frameLabel}
                        </div>
                        {card.pairedLabel.aiDescription ? (
                          <div className="text-xs font-mono text-slate-700 truncate">
                            {card.pairedLabel.aiDescription}
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-500 italic">
                            (label crop captured — text was unreadable)
                          </div>
                        )}
                        {card.pairedLabel.aiDescription && card.pairedLabel.aiDescription !== form.description && (
                          <button
                            type="button"
                            className="mt-0.5 text-[11px] text-blue-700 hover:underline self-start"
                            onClick={() => upd('description', card.pairedLabel.aiDescription)}
                            data-testid="card-paired-label-use-btn"
                          >
                            Use as description ↑
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <Input
                    value={form.description}
                    onChange={e => upd('description', e.target.value)}
                    placeholder={card.frameType === 'isolator' ? 'e.g. MAIN SWITCH 100A 4-POLE' : 'e.g. LIGHTING GROUND FLOOR'}
                    className="mt-1.5 font-mono uppercase text-sm"
                    data-testid="card-description-input"
                  />
                  <div className="mt-1 h-4">{fieldHint('description')}</div>
                </div>

                {/* Breaker-only fields. Hidden for circuit_label frames
                    because those frames hold ONLY the description
                    card; the AI shouldn't be asked to read MCB
                    type / rating / RCD off a paper label that doesn't
                    contain that data. Isolator frames also get a
                    pared-down spec (no MCB curve). */}
                {(!card.frameType || card.frameType === 'breaker') && (() => {
                  // BS EN drives which spec rows are relevant:
                  //   · 60898 = plain MCB → hide RCD rating
                  //   · 61008 = standalone RCD → hide MCB type + rating
                  //   · 61009 = RCBO (combined) → both visible
                  const bs = (form.bsEn || '').replace(/[^\d-]/g, '');
                  const isStandaloneRcd = bs === '61008';
                  const isPlainMcb = bs === '60898';
                  return (
                  <>
                    {!isStandaloneRcd && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label>MCB type</Label>
                          <Select value={form.mcbType || 'B'} onValueChange={v => upd('mcbType', v)}>
                            <SelectTrigger className="mt-1.5" data-testid="card-mcbtype-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="B">B</SelectItem>
                              <SelectItem value="C">C</SelectItem>
                              <SelectItem value="D">D</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="mt-1 h-4">{fieldHint('mcbType')}</div>
                        </div>
                        <div>
                          <Label>MCB rating</Label>
                          <Input
                            value={form.mcbRating}
                            onChange={e => upd('mcbRating', e.target.value)}
                            placeholder="32A"
                            className="mt-1.5"
                            data-testid="card-mcbrating-input"
                          />
                          <div className="mt-1 h-4">{fieldHint('mcbRating')}</div>
                        </div>
                      </div>
                    )}

                    {!isPlainMcb && (
                      <div className="mt-2">
                        <Label>RCD rating</Label>
                        <Input
                          value={form.rcdRating}
                          onChange={e => upd('rcdRating', e.target.value)}
                          placeholder="30mA (or leave blank if no RCD)"
                          className="mt-1.5"
                          data-testid="card-rcdrating-input"
                        />
                        <div className="mt-1 h-4">{fieldHint('rcdRating')}</div>
                      </div>
                    )}

                    {isStandaloneRcd && (
                      <div className="mt-2 text-[11px] text-slate-500 italic">
                        BS EN 61008 = standalone RCD → MCB type & rating are N/A.
                      </div>
                    )}
                    {isPlainMcb && (
                      <div className="mt-2 text-[11px] text-slate-500 italic">
                        BS EN 60898 = plain MCB → RCD rating is N/A.
                      </div>
                    )}

                    <div className="mt-2">
                      <Label>BS EN standard</Label>
                      <Input
                        value={form.bsEn}
                        onChange={e => upd('bsEn', e.target.value)}
                        placeholder="e.g. 60898 (MCB), 61008 (RCD), 61009 (RCBO)"
                        className="mt-1.5 font-mono"
                        list="bs-en-options"
                        data-testid="card-bsen-input"
                      />
                      <datalist id="bs-en-options">
                        {BS_EN_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </datalist>
                      <div className="mt-1 h-4">{fieldHint('bsEn')}</div>
                    </div>

                    <div className="mt-2">
                      <Label>Breaking capacity (kA)</Label>
                      <Input
                        value={form.breakingCapacityKa}
                        onChange={e => upd('breakingCapacityKa', e.target.value)}
                        placeholder="e.g. 6kA / 10kA — printed alongside BS EN"
                        className="mt-1.5 font-mono"
                        list="breaking-capacity-options"
                        data-testid="card-breaking-capacity-input"
                      />
                      <datalist id="breaking-capacity-options">
                        <option value="6kA" />
                        <option value="10kA" />
                        <option value="15kA" />
                        <option value="25kA" />
                      </datalist>
                      <div className="mt-1 h-4">{fieldHint('breakingCapacityKa')}</div>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <Checkbox
                        id="card-3pole"
                        checked={!!form.isThreePole}
                        onCheckedChange={v => upd('isThreePole', !!v)}
                        data-testid="card-isthreepole-checkbox"
                      />
                      <Label htmlFor="card-3pole" className="cursor-pointer select-none">
                        3-pole breaker (three-phase)
                      </Label>
                    </div>
                  </>
                  );
                })()}

                {card.frameType === 'isolator' && (
                  <div className="mt-2">
                    <Label>BS EN standard</Label>
                    <Input
                      value={form.bsEn}
                      onChange={e => upd('bsEn', e.target.value)}
                      placeholder="e.g. 60947-2 (isolator)"
                      className="mt-1.5 font-mono"
                      data-testid="card-isolator-bsen-input"
                    />
                  </div>
                )}

                {card.frameType === 'circuit_label' && (
                  <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                    Label frames carry only the printed circuit description card.
                    The breaker's spec (MCB type / rating / BS EN) lives on the
                    matching breaker card — fix it there if needed.
                  </p>
                )}

                {/* Derived-from-rules panel */}
                {(() => {
                  const derivedEntries = Object.entries(derived).filter(([k]) => k !== '_matchedRules');
                  if (derivedEntries.length === 0) return null;
                  return (
                    <div
                      className="mt-3 rounded-md border border-blue-200 bg-blue-50/50 p-3 space-y-1.5"
                      data-testid="derived-fields-panel"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-900 flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> Derived from rules
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`${base}/certificates/rules`)}
                          className="text-[10px] text-blue-700 hover:text-blue-900 underline"
                        >
                          edit rules
                        </button>
                      </div>
                      {derivedEntries.map(([k, v]) => (
                        <div key={k} className="text-xs flex items-baseline justify-between font-mono">
                          <span className="text-slate-600">{k}</span>
                          <span className="text-emerald-700 font-semibold">{v}</span>
                        </div>
                      ))}
                      {(derived._matchedRules || []).length > 0 && (
                        <div className="pt-1 border-t border-blue-100 text-[10px] text-blue-700 italic">
                          via {(derived._matchedRules).map(r => r.name).join(' · ')}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <div className="flex items-center gap-1 sm:gap-2 order-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSkip}
                    disabled={submitting}
                    data-testid="card-skip-btn"
                  >
                    <SkipForward className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Skip</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDelete}
                    disabled={submitting}
                    className="text-slate-400 hover:text-red-600"
                    data-testid="card-delete-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2 order-2 ml-auto">
                  <PromoteToLibraryButton
                    card={card}
                    currentForm={form}
                    disabled={submitting || isSpare || !card?.cropUrl}
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={`${isSpare ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white font-semibold`}
                    data-testid="card-submit-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    <span className="sm:hidden">{isSpare ? 'Confirm' : 'Save'}</span>
                    <span className="hidden sm:inline">{isSpare ? 'Confirm spare & next' : 'Save & next'}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </section>

      {/* Recent activity */}
      {sessionLabeled > 0 && (
        <div className="text-center text-sm text-slate-500" data-testid="session-labeled">
          🎯 You&apos;ve labelled <span className="font-semibold text-slate-800">{sessionLabeled}</span> card{sessionLabeled === 1 ? '' : 's'} this session — every one improves the AI.
        </div>
      )}

      {/* Board topology grids — one per uploaded photo */}
      {boards.length > 0 && (
        <div className="space-y-4" data-testid="board-grids-section">
          <h2 className="font-display uppercase tracking-wide text-slate-900 text-sm">3. Board topology</h2>
          {boards.map(board => {
            const boardCards = cards.filter(c => c.boardId === board.id);
            return (
              <BoardGrid
                key={board.id}
                board={board}
                cards={boardCards}
                onCardClick={jumpToCard}
                onDelete={async (b) => {
                  // Engineer-driven cleanup: nuke a topology + its
                  // training cards in one go (used for stuck queues
                  // built from poor-quality photos with `0 ways`).
                  const cardCount = cards.filter(c => c.boardId === b.id).length;
                  const ok = window.confirm(
                    `Delete this board topology and ${cardCount} training card${cardCount === 1 ? '' : 's'}? This cannot be undone.`,
                  );
                  if (!ok) return;
                  try {
                    const res = await deleteTrainingBoard(b.id);
                    toast.success(`Deleted board · ${res.deletedCards} card${res.deletedCards === 1 ? '' : 's'} cascaded`);
                    setBoards(prev => prev.filter(x => x.id !== b.id));
                    setCards(prev => prev.filter(c => c.boardId !== b.id));
                    // If the active card was on this board, snap back
                    // to whatever pending card is now first.
                    setActiveIdx(0);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Delete failed');
                  }
                }}
              />
            );
          })}
        </div>
      )}
      {/* Per-card frame-adjustment dialog. Lives at page level so the
          drag handlers don't get clipped by the card's overflow rules.
          Mounted only when a pending card is active. */}
      {card && (
        <CertTrainFrameAdjuster
          open={adjusterOpen}
          onOpenChange={setAdjusterOpen}
          card={card}
          prefetchedPhotoUrl={
            prefetchedPhotoCardIdRef.current === card.id ? prefetchedPhotoUrl : null
          }
          onApplied={(updated) => {
            // Swap the AI guess into the active card in place so the
            // form re-hydrates from the freshly-extracted values
            // without losing the engineer's queue position.
            setCards(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
          }}
        />
      )}
    </div>
  );
};

export default CertTrain;
